import React from 'react';
import { CSSTransition } from 'react-transition-group';

import { cx } from 'helpers/StylingHelpers';

import type { DataTestId } from 'types';

import styles from './ModalContent.module.scss';

interface Props extends DataTestId {
  //
  className?: string,
  //
  isOpen: boolean,
  //
  position: any,
  isResponsive: boolean,
  isFullScreen: boolean,
  isWide: boolean,
  depth: number,
  //
  onEntering?: () => void,
  onEntered?: () => void,
  onExiting?: () => void,
  onExited?: () => void,
  //
  children?: React.ReactNode
  //
}

const ModalContent = React.forwardRef<HTMLDivElement, Props>((
  props: Props,
  ref
) => {
  const {
    className,
    //
    isOpen,
    //
    position,
    isResponsive,
    isFullScreen,
    isWide,
    depth,
    //
    onEntering = () => {},
    onEntered = () => {},
    onExiting = () => {},
    onExited = () => {},
    //
    children,
    //
    dataTestId,
    //
    ...rest
  } = props;

  // Refs

  const contentRef = React.useRef<HTMLDivElement>(null);

  // Hooks

  const [isEntered, setIsEntered] = React.useState(false);

  // Props

  const maxWidth = ((rest as any).style || {}).maxWidth
    || (isWide ? styles.maxWidthWide : styles.maxWidth);

  // Render

  return (
    <CSSTransition
      in={isOpen}
      timeout={{
        enter: 400,
        exit: 200,
      }}
      appear
      onEntering={onEntering}
      onEntered={() => {
        setIsEntered(true);
        onEntered();
      }}
      onExiting={onExiting}
      onExited={onExited}
      classNames={{
        //
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        //
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
        //
      }}
    >

      <div
        ref={contentRef}
        className={cx(
          styles.root,
          { [styles[position]]: !!position },
          { [styles.desktop]: !isResponsive },
          { [styles.responsive]: isResponsive },
          { [styles.fullScreen]: isFullScreen },
          { [styles.wide]: isWide },
          { [styles.enterDone]: isEntered }, // hack to ensure exit animation works properly
          className,
        )}
        data-testid="modal.content"
        {...rest}
        style={{
          ...(isResponsive ? {} : {
            top: `${depth * 20}px`,
            maxWidth: `calc(${(maxWidth)} + ${depth * 40}px)`,
          }),
          ...((rest as any).style || {}),
        }}
      >
        {children}
      </div>

    </CSSTransition>
  );
});

export default ModalContent;
